// A projeção das ofertas de rent a car para a view que a `RentalResultsPage`
// desenha — pura, sem React: a página conhece FATOS (câmbio, política, total)
// e rótulos já em prosa; quem sabe idioma e moeda é o host. Uma fonte para as
// DUAS superfícies da página: a bancada do web-chat e o takeover "Ver mais
// opções" da conversa (genui-kit).
//
// A entrada é ESTRUTURAL (`RentalOfferSource`): o kit não importa o wire do
// BFF nem o do agente, e as duas formas cabem nela sem adaptador.
import type { RentalMapView, RentalOfferView } from '../models.js';
import { distanceKm } from '../../shared/distance_km.js';
import type { RentalFilterableOffer, RentalFilterCatalog } from './rental_results_filters.js';
import {
  rentalStopLabelsOf,
  type RentalSearchedPlace,
  type RentalStopEntry,
} from './rental_stop_label.js';

export type RentalMoney = { amount: number; currency: string };

type RentalCoordinates = { latitude: number; longitude: number };

/** O que a projeção lê de uma oferta. */
export interface RentalOfferSource {
  offerId: string;
  vehicleName?: string;
  /** O fornecedor marcou o modelo como "ou similar" (o BFF o tira do nome). */
  orSimilar?: boolean;
  thumbUrl?: string;
  category: string;
  rentalCompany: string;
  /** O código da locadora (ex.: "MO"), o mesmo `id` da faceta. Ofertas antigas
   *  (ou da conversa) não o trazem: aí a faceta é achada pelo nome. */
  companyCode?: string;
  companyLogoUrl?: string;
  transmission: 'AUTOMATIC' | 'MANUAL';
  airConditioning: boolean;
  /** O TOTAL do período, não a diária. */
  price: RentalMoney;
  days?: number;
  /** O nome cru da agência; `*AgencyLabel` é o tratado pelo BFF, quando há. */
  pickUpAgency?: string;
  pickUpAgencyLabel?: string;
  pickUpAddress?: string;
  pickUpCoordinates?: RentalCoordinates;
  dropOffAgency?: string;
  dropOffAgencyLabel?: string;
  dropOffAddress?: string;
  dropOffCoordinates?: RentalCoordinates;
  withinPolicy: boolean;
}

/** Um ponto da busca como a página o resolveu: o rótulo do lugar BUSCADO, o
 *  tipo dele (que decide quando o card repete o rótulo no lugar do nome da
 *  agência, `rental_stop_label.ts`) e, quando o host as tem, as coordenadas
 *  que ancoram o mapa. */
type RentalResultsStop = RentalSearchedPlace;

/** O que a projeção lê da BUSCA além das ofertas: as facetas (cujos códigos
 *  viram as chaves dos filtros) e os dois pontos. Estrutural de propósito — a
 *  página do BFF (`RentalSearchPage`, web-chat) e o bloco da conversa
 *  (`metadata` + os pontos derivados das ofertas) cabem nele sem adaptador. */
export interface RentalResultsSource {
  facets: {
    /** `count`/`lowerValue` são do conjunto INTEIRO da busca (o servidor
     *  conta antes de paginar): é deles que sai o catálogo estável dos filtros
     *  (`rentalFilterCatalogOf`). */
    categories: readonly {
      id: string;
      name: string;
      count?: number;
      lowerValue?: RentalMoney;
    }[];
    companies: readonly { id: string; name: string; imageUrl?: string }[];
  };
  pickup: RentalResultsStop;
  dropoff: RentalResultsStop;
}

/** As palavras que a projeção precisa do host — resolvidas UMA vez por página,
 *  não por oferta (`t()` por card na lista inteira era custo sem motivo). */
export interface RentalResultsViewCopy {
  /** O sufixo "ou similar" que troca o "OR SIMILAR" cru do fornecedor. */
  orSimilar: string;
  /** A linha sob o total: a DIÁRIA (total ÷ dias) e o período, já formatados
   *  pelo host, que conhece plural e moeda. O filtro "Diária" corta por esse
   *  número, e ele não aparecia em lugar nenhum do card (#3609 C3). */
  priceDetail(days: number, daily: RentalMoney): string;
  /** Só a diária ("R$ 163/diária"), a linha sob o total no card de desktop. */
  dailyPrice(daily: RentalMoney): string;
  /** A marca de fora da política — o rótulo É a marca (regra do kit). */
  outOfPolicy: string;
  /** O selo do menor total da página. */
  cheapestBadge: string;
  /** O TOTAL formatado ("R$ 489,00") — moeda é do host: o web-chat e a
   *  conversa têm formatadores próprios, e a mesma oferta tem de sair igual
   *  nas duas telas. */
  priceLabel(price: RentalMoney): string;
  /** O local quando a oferta não traz nome nem endereço da agência
   *  ("Agência Localiza") — o bloco do local nunca fica vazio. */
  agencyFallback(company: string): string;
}

/** O código de refino da faceta cujo NOME a oferta carrega (`category` e
 *  `rentalCompany` são rótulos); sem faceta, o próprio rótulo serve de chave —
 *  o filtro client-side só precisa de igualdade. */
function facetIdOf(facets: readonly { id: string; name: string }[], name: string): string {
  return facets.find((facet) => facet.name === name)?.id ?? name;
}

/** "Tesla Model" com `orSimilar` → "Tesla Model ou similar" (o BFF manda a
 *  marca como dado, #5529); o "OR SIMILAR" cru no nome, que a oferta do agente
 *  ainda traz, vira o mesmo. E nada quando o fornecedor repete a categoria no
 *  nome ("Econômico" / "Econômico") — a mesma regra do card conversacional
 *  (`VehicleRow`). Exportada para o checkout nomear o carro pela mesma palavra
 *  em toda a jornada. */
export function vehicleLabelOf(
  offer: Pick<RentalOfferSource, 'vehicleName' | 'orSimilar' | 'category'>,
  orSimilar: string,
): string | undefined {
  const name = offer.vehicleName?.trim();
  if (!name || name.toLowerCase() === offer.category.trim().toLowerCase()) return undefined;
  return offer.orSimilar ? `${name} ${orSimilar}` : replaceOrSimilar(name, orSimilar);
}

const OR_SIMILAR = /\sOR SIMILAR\b/i;

/** O primeiro " OR SIMILAR" (com todo o espaço antes) vira " <orSimilar>". Acha o
 *  marcador por UM espaço e volta pela corrida, em vez de `\s+OR SIMILAR`, cujo
 *  `\s+` inicial reescaneava cada corrida de espaço (S5852). */
function replaceOrSimilar(name: string, orSimilar: string): string {
  const at = name.search(OR_SIMILAR);
  if (at === -1) return name;
  let start = at;
  while (start > 0 && /\s/.test(name.charAt(start - 1))) start--;
  return `${name.slice(0, start)} ${orSimilar}${name.slice(at + ' OR SIMILAR'.length)}`;
}

type StopSide = 'pickup' | 'dropoff';

function agencyOf(offer: RentalOfferSource, side: StopSide): RentalStopEntry['agency'] {
  const [name, label, address, coordinates] =
    side === 'pickup'
      ? [offer.pickUpAgency, offer.pickUpAgencyLabel, offer.pickUpAddress, offer.pickUpCoordinates]
      : [
          offer.dropOffAgency,
          offer.dropOffAgencyLabel,
          offer.dropOffAddress,
          offer.dropOffCoordinates,
        ];
  return {
    ...(name ? { name } : {}),
    ...(label ? { label } : {}),
    ...(address ? { address } : {}),
    ...(coordinates ? { coordinates } : {}),
  };
}

/** O texto do local de UMA ponta de cada oferta. É da LISTA, não da oferta: a
 *  colisão de duas agências da mesma locadora sob o mesmo rótulo só se vê com
 *  todas em mãos. */
function stopLabelsOf(
  offers: readonly RentalOfferSource[],
  page: RentalResultsSource,
  copy: RentalResultsViewCopy,
  side: StopSide,
): string[] {
  return rentalStopLabelsOf(
    offers.map((offer) => ({
      companyKey: offer.companyCode ?? offer.rentalCompany,
      companyName: offer.rentalCompany,
      agency: agencyOf(offer, side),
    })),
    page[side],
    (company) => copy.agencyFallback(company),
  );
}

/** As duas linhas da diária: com o período (celular) e sem ele (desktop). */
function dailyLabelsOf(
  days: number,
  total: RentalMoney,
  copy: RentalResultsViewCopy,
): Pick<RentalOfferView, 'priceDetail' | 'dailyPriceLabel'> {
  const daily = { amount: total.amount / days, currency: total.currency };
  return { priceDetail: copy.priceDetail(days, daily), dailyPriceLabel: copy.dailyPrice(daily) };
}

function toOfferView(
  offer: RentalOfferSource,
  stops: { pickup: string; dropoff: string },
  copy: RentalResultsViewCopy,
  cheapest: boolean,
  selectable: boolean,
): RentalOfferView {
  const vehicleLabel = vehicleLabelOf(offer, copy.orSimilar);
  return {
    id: offer.offerId,
    categoryLabel: offer.category,
    ...(vehicleLabel ? { vehicleLabel } : {}),
    ...(offer.thumbUrl ? { thumbnailUrl: offer.thumbUrl } : {}),
    company: {
      name: offer.rentalCompany,
      ...(offer.companyCode ? { code: offer.companyCode } : {}),
      ...(offer.companyLogoUrl ? { logoUrl: offer.companyLogoUrl } : {}),
    },
    transmission: offer.transmission === 'AUTOMATIC' ? 'automatic' : 'manual',
    airConditioning: offer.airConditioning,
    // O texto vem da regra do local (`stopLabelsOf`). A coordenada é a da
    // AGÊNCIA (o pino do mapa, #3650), nunca a do lugar buscado: essa é a âncora.
    pickup: {
      agencyLabel: stops.pickup,
      ...(offer.pickUpAddress ? { addressLabel: offer.pickUpAddress } : {}),
      ...(offer.pickUpCoordinates ? { coordinates: offer.pickUpCoordinates } : {}),
    },
    dropoff: {
      agencyLabel: stops.dropoff,
      ...(offer.dropOffAddress ? { addressLabel: offer.dropOffAddress } : {}),
      ...(offer.dropOffCoordinates ? { coordinates: offer.dropOffCoordinates } : {}),
    },
    priceLabel: copy.priceLabel(offer.price),
    ...(offer.days ? dailyLabelsOf(offer.days, offer.price, copy) : {}),
    // Só o menor total ganha selo: a página não traz mérito ("recomendado")
    // nem distância, então o ordenador `recommended` preserva a curadoria do
    // fornecedor e o selo do preço é o único eixo com fato por trás.
    badges: cheapest
      ? [{ id: 'cheapest', label: copy.cheapestBadge, tone: 'unknown', icon: 'cheapest' }]
      : [],
    ...(offer.withinPolicy ? {} : { outOfPolicyLabel: copy.outOfPolicy }),
    selectable,
  };
}

/** As ofertas da página como o kit as filtra e ordena. `cheapest` é o menor
 *  TOTAL entre todas — inclusive fora da política: o selo diz preço, não mérito.
 *  `selectable: false` tira o `Selecionar` de todos os cards — a conversa
 *  somente-leitura (card congelado), onde não há a quem entregar a escolha. */
export function toRentalFilterableOffers(
  offers: readonly RentalOfferSource[],
  page: RentalResultsSource,
  copy: RentalResultsViewCopy,
  selectable = true,
  /** A página da busca que trouxe cada oferta (`useRentalOfferPages.pageOf`). */
  pageOf?: (offerId: string) => number,
): RentalFilterableOffer[] {
  const cheapestTotal = Math.min(...offers.map((offer) => offer.price.amount));
  const pickups = stopLabelsOf(offers, page, copy, 'pickup');
  const dropoffs = stopLabelsOf(offers, page, copy, 'dropoff');
  const { latitude, longitude } = page.pickup;
  const anchor =
    latitude === undefined || longitude === undefined ? undefined : { latitude, longitude };
  return offers.map((offer, index) => ({
    ...(anchor && offer.pickUpCoordinates
      ? { distance: distanceKm(anchor, offer.pickUpCoordinates) }
      : {}),
    ...(pageOf ? { page: pageOf(offer.offerId) } : {}),
    offer: toOfferView(
      offer,
      { pickup: pickups[index] ?? '', dropoff: dropoffs[index] ?? '' },
      copy,
      offer.price.amount === cheapestTotal,
      selectable,
    ),
    withinPolicy: offer.withinPolicy,
    totalPrice: offer.price.amount,
    ...(offer.days ? { dailyPrice: offer.price.amount / offer.days } : {}),
    categoryId: facetIdOf(page.facets.categories, offer.category),
    // O nome da locadora pode vir com a filial e não casar com o da faceta; o
    // código casa sempre.
    companyId: offer.companyCode ?? facetIdOf(page.facets.companies, offer.rentalCompany),
  }));
}

/** O catálogo dos filtros pelas facetas do servidor, que contam a busca
 *  INTEIRA: as categorias com oferta (o servidor manda as zeradas também) e o
 *  menor total de cada uma, e todas as locadoras. Derivado só das ofertas
 *  carregadas, o painel mudava enquanto a rolagem trazia páginas (#4513, I2). */
export function rentalFilterCatalogOf(facets: RentalResultsSource['facets']): RentalFilterCatalog {
  return {
    categories: facets.categories
      .filter((category) => category.count === undefined || category.count > 0)
      .map((category) => ({
        id: category.id,
        label: category.name,
        ...(category.lowerValue ? { fromPrice: category.lowerValue.amount } : {}),
      })),
    companies: facets.companies.map((company) => ({
      id: company.id,
      label: company.name,
      ...(company.imageUrl ? { logoUrl: company.imageUrl } : {}),
    })),
  };
}

/** A moeda da lista: a da primeira oferta (a página é de UMA praça, uma moeda). */
export function rentalPageCurrency(offers: readonly RentalOfferSource[]): string {
  return offers[0]?.price.currency ?? 'BRL';
}

/** O mapa da página: a âncora do lugar de RETIRADA buscado. Os pinos das
 *  agências não moram aqui — o kit os deriva das ofertas EM CENA
 *  (`RentalStopView.coordinates`), para o filtro da lista filtrar o mapa. Sem
 *  token ou sem coordenadas o kit mostra o fallback nomeado. */
export function rentalMapViewOf(
  page: Pick<RentalResultsSource, 'pickup'>,
  accessToken: string | undefined,
  copy: Pick<RentalMapView, 'label' | 'fallbackLabel'>,
): RentalMapView {
  const { latitude, longitude, label } = page.pickup;
  const anchored = latitude !== undefined && longitude !== undefined;
  return {
    available: accessToken !== undefined && anchored,
    ...(accessToken ? { accessToken } : {}),
    ...(anchored ? { anchor: { coordinates: { latitude, longitude }, label } } : {}),
    ...copy,
  };
}
